import { RiskFieldsTypeEnum, TemplateFieldTypeEnum } from '@/__generated__/graphql';
import {
  CIRCUMFERENTIAL_CRACKING_MAP,
  COMPRESSION_SET_FENDER_MAP,
  LONGITUDINAL_CRACKING_MAP,
  RUBBER_HARDNESS_MAP,
  SAGGING_FENDER_MAP,
  YEARS_MULTIPLIER_MAP,
} from '@/components/Analysis/RiskAssessment/constants';
import { CustomField } from '@/components/shared/CustomField';
import { RiskFactorInputTabs } from '@/components/shared/RiskFactorInputTabs';
import Stack from '@mui/material/Stack';
import { useCallback, useState } from 'react';
import { textFieldStyles } from '../styles';

type Props = {
  shipsAtBerth: string;
  onChange: ({
    pofValues,
    inputs,
  }: {
    pofValues?: { [key: string]: string };
    inputs?: {
      id: string;
      value: string;
    }[];
  }) => void;
};

export const RubberFenderPoF = ({ shipsAtBerth, onChange }: Props) => {
  const [selectedValues, setSelectedValues] = useState<{ [key: string]: string | null }>({
    E: '',
    N: `${shipsAtBerth}`,
    I: '',
    Y: '',
    H: '',
    LC: '',
    CC: '',
    SF: '',
    CS: '',
  });

  const changeField = (value: string, fieldId: string) => {
    setSelectedValues((current) => ({
      ...current,
      [fieldId]: value,
    }));
  };

  const changeRiskFactor = useCallback(
    (selectedRiskFactorValues: { [key: string]: string | null }) => {
      const inputValues = [
        { id: 'risk_base_pof_value', value: selectedValues.E ?? '' },
        { id: 'risk_ships_accepted_annually_at_berth', value: selectedValues.N ?? '' },
        { id: 'risk_inspection_performed', value: selectedValues.I ?? '' },
        { id: 'risk_years_in_service', value: selectedValues.Y ?? '' },
        { id: 'risk_hardness_percentage', value: selectedValues.H ?? '' },
        { id: 'risk_longitudinal_cracking', value: selectedValues.LC ?? '' },
        { id: 'risk_circumferential_cracking', value: selectedValues.CC ?? '' },
        { id: 'risk_sagging_fender', value: selectedValues.SF ?? '' },
        { id: 'risk_compression_set_fender', value: selectedValues.CS ?? '' },
        {
          id: 'risk_safety_value_pof_c',
          value: selectedRiskFactorValues.risk_safety_calculated_value_pof ?? '',
        },
        {
          id: 'risk_environment_value_pof_c',
          value: selectedRiskFactorValues.risk_environment_calculated_value_pof ?? '',
        },
        {
          id: 'risk_public_impact_value_pof_c',
          value: selectedRiskFactorValues.risk_public_impact_calculated_value_pof ?? '',
        },
        {
          id: 'risk_direct_cost_value_pof_c',
          value: selectedRiskFactorValues.risk_direct_cost_calculated_value_pof ?? '',
        },
        {
          id: 'risk_business_loss_value_pof_c',
          value: selectedRiskFactorValues.risk_business_loss_calculated_value_pof ?? '',
        },
      ];
      const pofValues: { [key: string]: string } = {
        risk_safety_calculated_value_pof:
          selectedRiskFactorValues.risk_safety_calculated_value_pof ?? '',
        risk_environment_calculated_value_pof:
          selectedRiskFactorValues.risk_environment_calculated_value_pof ?? '',
        risk_public_impact_calculated_value_pof:
          selectedRiskFactorValues.risk_public_impact_calculated_value_pof ?? '',
        risk_direct_cost_calculated_value_pof:
          selectedRiskFactorValues.risk_direct_cost_calculated_value_pof ?? '',
        risk_business_loss_calculated_value_pof:
          selectedRiskFactorValues.risk_business_loss_calculated_value_pof ?? '',
      };
      const filteredInputValues = inputValues.filter((input) => input.value !== '');
      onChange({ inputs: filteredInputValues, pofValues });
    },
    [onChange, selectedValues]
  );

  return (
    // TODO: map from list
    <Stack spacing={3}>
      <Stack direction="row" gap={3} justifyContent="space-between" width="100%">
        <Stack direction="row" gap={3} alignItems="flex-end" width="100%">
          <CustomField
            location={RiskFieldsTypeEnum.Pof.toString()}
            sx={textFieldStyles}
            fieldType={TemplateFieldTypeEnum.Input}
            fieldName="Base PoF value (E)"
            onChange={(value) => changeField(value, 'E')}
            commentIdentifier="risk_base_pof_value"
            placeholder="Enter value or will be taken from inspection"
            initialValue=""
          />
        </Stack>
        <Stack direction="row" gap={3} alignItems="flex-end" width="100%">
          <CustomField
            location={RiskFieldsTypeEnum.Pof.toString()}
            sx={textFieldStyles}
            fieldType={TemplateFieldTypeEnum.Input}
            fieldName="Ships accepted annually at berth (N)"
            onChange={(value) => changeField(value, 'N')}
            commentIdentifier="risk_ships_accepted_annually_at_berth"
            initialValue={shipsAtBerth}
          />
        </Stack>
      </Stack>
      <Stack direction="row" gap={3} justifyContent="space-between" width="100%">
        <Stack direction="row" gap={3} alignItems="flex-end" width="100%">
          <CustomField
            location={RiskFieldsTypeEnum.Pof.toString()}
            fieldType={TemplateFieldTypeEnum.SingleSelect}
            fieldName="Inspection performed"
            fieldOptions={['Yes', 'No']}
            onChange={(value) => changeField(value, 'I')}
            commentIdentifier="risk_inspection_performed"
            initialValue=""
          />
        </Stack>
        <Stack direction="row" gap={3} alignItems="flex-end" width="100%">
          {selectedValues.I === 'No' && (
            <CustomField
              location={RiskFieldsTypeEnum.Pof.toString()}
              fieldType={TemplateFieldTypeEnum.SingleSelect}
              fieldOptions={Object.keys(YEARS_MULTIPLIER_MAP)}
              fieldName="Years in service multiplier"
              onChange={(value) => changeField(value, 'Y')}
              initialValue=""
            />
          )}
          {selectedValues.I === 'Yes' && (
            <CustomField
              location={RiskFieldsTypeEnum.Pof.toString()}
              fieldType={TemplateFieldTypeEnum.SingleSelect}
              fieldOptions={Object.keys(RUBBER_HARDNESS_MAP)}
              fieldName="Increase in hardness(percentage %)"
              onChange={(value) => changeField(value, 'H')}
              commentIdentifier="risk_hardness_percentage"
              initialValue=""
            />
          )}
        </Stack>
      </Stack>
      {selectedValues.I === 'Yes' && (
        <>
          <Stack direction="row" gap={3} justifyContent="space-between" width="100%">
            <Stack direction="row" gap={3} alignItems="flex-end" width="100%">
              <CustomField
                location={RiskFieldsTypeEnum.Pof.toString()}
                fieldType={TemplateFieldTypeEnum.SingleSelect}
                fieldName="Longitudinal cracking"
                fieldOptions={Object.keys(LONGITUDINAL_CRACKING_MAP)}
                onChange={(value) => changeField(value, 'LC')}
                commentIdentifier="risk_longitudinal_cracking"
                initialValue=""
              />
            </Stack>
            <Stack direction="row" gap={3} alignItems="flex-end" width="100%">
              <CustomField
                location={RiskFieldsTypeEnum.Pof.toString()}
                fieldType={TemplateFieldTypeEnum.SingleSelect}
                fieldName="Circumferential cracking"
                fieldOptions={Object.keys(CIRCUMFERENTIAL_CRACKING_MAP)}
                onChange={(value) => changeField(value, 'CC')}
                commentIdentifier="risk_circumferential_cracking"
                initialValue=""
              />
            </Stack>
          </Stack>
          <Stack direction="row" gap={3} justifyContent="space-between" width="100%">
            <Stack direction="row" gap={3} alignItems="flex-end" width="100%">
              <CustomField
                location={RiskFieldsTypeEnum.Pof.toString()}
                fieldType={TemplateFieldTypeEnum.SingleSelect}
                fieldName="Sagging fender multiplier"
                fieldOptions={Object.keys(SAGGING_FENDER_MAP)}
                onChange={(value) => changeField(value, 'SF')}
                commentIdentifier="risk_sagging_fender"
                initialValue=""
              />
            </Stack>
            <Stack direction="row" gap={3} alignItems="flex-end" width="100%">
              <CustomField
                location={RiskFieldsTypeEnum.Pof.toString()}
                fieldType={TemplateFieldTypeEnum.SingleSelect}
                fieldName="Compression set fender multiplier"
                fieldOptions={Object.keys(COMPRESSION_SET_FENDER_MAP)}
                onChange={(value) => changeField(value, 'CS')}
                commentIdentifier="risk_compression_set_fender"
                initialValue=""
              />
            </Stack>
          </Stack>
        </>
      )}
      <RiskFactorInputTabs parentTab="pof" onInputChange={changeRiskFactor} />
    </Stack>
  );
};
